import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { gapi, errMsg } from "../api";
import { AuthShell, Field, Submit, Notice, PASSWORD_HINT } from "../components/AuthShell";

function RecoveryCodes({ codes }) {
  return (
    <div data-testid="g-recovery-codes">
      <Notice kind="ok">Account titolare attivato.</Notice>
      <p className="text-sm text-anthracite/70">Conserva questi <b>codici di recupero</b> in un luogo sicuro. Ognuno vale una sola volta e permette di accedere se non ricevi il codice via email. Non verranno mostrati di nuovo.</p>
      <div className="grid grid-cols-2 gap-2 my-5 font-mono text-sm">
        {codes.map((c) => <div key={c} className="rounded-lg bg-white border border-border-subtle px-3 py-2 text-center">{c}</div>)}
      </div>
      <Link to="/gestionale/accesso" className="block text-center rounded-full bg-brand text-white font-semibold py-3.5" data-testid="g-setup-go-login">Ho salvato i codici, vai all'accesso</Link>
    </div>
  );
}

export default function Attivazione() {
  const [params] = useSearchParams();
  const token = params.get("token");
  const [status, setStatus] = useState(null);
  const [nome, setNome] = useState("");
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [msg, setMsg] = useState({});
  const [codes, setCodes] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => { gapi.get("/auth/setup/status").then((r) => setStatus(r.data)).catch(() => setStatus({})); }, []);

  const request = async () => {
    setLoading(true); setMsg({});
    try {
      const { data } = await gapi.post("/auth/setup/request");
      setMsg({ ok: data.emailInviata === false ? "Richiesta registrata, ma l'invio email non è riuscito. Riprova tra qualche minuto." : `Se il titolare non è ancora attivo, il link è stato inviato a ${status?.emailTitolare}. Controlla anche lo spam.` });
    } catch (e) { setMsg({ err: errMsg(e) }); } finally { setLoading(false); }
  };

  const complete = async (e) => {
    e.preventDefault();
    if (pw !== pw2) return setMsg({ err: "Le password non coincidono" });
    setLoading(true); setMsg({});
    try {
      const { data } = await gapi.post("/auth/setup/complete", { token, nome, password: pw });
      setCodes(data.codiciRecupero);
    } catch (e2) { setMsg({ err: errMsg(e2) }); } finally { setLoading(false); }
  };

  if (codes) return <AuthShell title="Attivazione completata" testid="g-setup-done"><RecoveryCodes codes={codes} /></AuthShell>;

  if (token) {
    return (
      <AuthShell title="Attiva l'account Titolare" subtitle="Scegli il tuo nome e una password personale." testid="g-setup-form">
        {msg.err && <Notice testid="g-setup-error">{msg.err}</Notice>}
        <form onSubmit={complete}>
          <Field label="Nome e cognome" value={nome} onChange={(e) => setNome(e.target.value)} required data-testid="g-setup-nome" />
          <Field label="Password" type="password" value={pw} onChange={(e) => setPw(e.target.value)} autoComplete="new-password" required data-testid="g-setup-password" />
          <Field label="Ripeti password" type="password" value={pw2} onChange={(e) => setPw2(e.target.value)} autoComplete="new-password" required data-testid="g-setup-password2" />
          <p className="text-xs text-anthracite/50 -mt-2 mb-5">{PASSWORD_HINT} L'accesso richiederà sempre anche un codice inviato via email.</p>
          <Submit loading={loading} data-testid="g-setup-submit">Attiva account</Submit>
        </form>
      </AuthShell>
    );
  }

  return (
    <AuthShell title="Prima attivazione" subtitle="Procedura sicura senza password predefinite." testid="g-setup-request">
      {msg.err && <Notice>{msg.err}</Notice>}
      {msg.ok && <Notice kind="ok" testid="g-setup-request-ok">{msg.ok}</Notice>}
      {status && status.needsSetup === false ? (
        <Notice kind="ok" testid="g-setup-already">Il titolare è già configurato. Usa la pagina di accesso.</Notice>
      ) : (
        <>
          <p className="text-sm text-anthracite/70 mb-6">Invieremo un link di attivazione, valido 24 ore e utilizzabile una sola volta, all'email del titolare <b>{status?.emailTitolare}</b>.</p>
          <button onClick={request} disabled={loading || !status} className="w-full rounded-full bg-brand text-white font-semibold py-3.5 hover:bg-brand-hover disabled:opacity-50" data-testid="g-setup-request-btn">Invia link di attivazione</button>
        </>
      )}
      <Link to="/gestionale/accesso" className="block mt-6 text-sm text-anthracite/50 hover:underline">Torna all'accesso</Link>
    </AuthShell>
  );
}
